import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Avatar,
  Chip,
  TextField,
  Button,
  Divider,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CommentIcon from "@mui/icons-material/Comment";
import MessageIcon from "@mui/icons-material/Message";
import type { Post, Comment } from "../types";
import { posts as postsApi, inquiries } from "../api/endpoints";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { alpha } from "@mui/material/styles";
import { CARD_TINT_GRADIENT } from "../theme/theme";

function timeAgo(dateStr: string, t: (key: string) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("feed.justNow");
  if (mins < 60) return `${mins} ${t("feed.minutesAgo")}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${t("feed.hoursAgo")}`;
  return `${Math.floor(hours / 24)} ${t("feed.daysAgo")}`;
}

// Each post type gets a distinct accent color for its left border, plus a
// matching gradient for its chip so the feed reads as colorful as Landing.
const POST_TYPE_META: Record<string, { color: string; gradient: string; key: string }> = {
  general_discussion: { color: "#0F2156", gradient: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)", key: "feed.generalDiscussion" },
  job_offer:          { color: "#B91C2C", gradient: "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)", key: "feed.jobOffer" },
  job_request:        { color: "#D97706", gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", key: "feed.jobRequest" },
  housing_offer:      { color: "#16A34A", gradient: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)", key: "feed.housingOffer" },
  housing_request:    { color: "#7C3AED", gradient: "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)", key: "feed.housingRequest" },
  friend_request:     { color: "#0891B2", gradient: "linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)", key: "feed.friendRequest" },
  other:              { color: "#6B7280", gradient: "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)", key: "feed.other" },
};

function getPostTypeMeta(type: string) {
  return POST_TYPE_META[type] ?? POST_TYPE_META["other"];
}

// Deterministic avatar background from name
const AVATAR_COLORS = ["#B91C2C", "#0F2156", "#D97706", "#16A34A", "#7C3AED", "#0891B2", "#DC2626"];
function avatarColor(letter: string): string {
  const idx = letter.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

interface PostCardProps {
  post: Post;
  onRefresh?: () => void;
}

export default function PostCard({ post, onRefresh }: PostCardProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count ?? 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const [inquireOpen, setInquireOpen] = useState(false);
  const [inquireMsg, setInquireMsg] = useState("");
  const [inquireLoading, setInquireLoading] = useState(false);
  const [inquireError, setInquireError] = useState<string | null>(null);

  const displayName = post.is_anonymous
    ? t("common.anonymous")
    : post.creator
    ? `${post.creator.first_name} ${post.creator.last_name}`
    : t("messages.unknown");

  const avatarLetter = post.is_anonymous ? "A" : displayName.charAt(0).toUpperCase();
  const meta = getPostTypeMeta(post.post_type);

  const handleLike = async () => {
    try {
      if (liked) {
        await postsApi.unlike(post.id);
        setLiked(false);
        setLikesCount((c) => Math.max(0, c - 1));
      } else {
        await postsApi.like(post.id);
        setLiked(true);
        setLikesCount((c) => c + 1);
      }
    } catch {
      // ignore
    }
  };

  const handleToggleComments = async () => {
    if (!commentsOpen && !commentsLoaded) {
      try {
        const data = await postsApi.listComments(post.id);
        setComments(data ?? []);
        setCommentsLoaded(true);
      } catch {
        // ignore
      }
    }
    setCommentsOpen((v) => !v);
  };

  const handleInquire = async () => {
    if (!user || !inquireMsg.trim()) return;
    setInquireLoading(true);
    try {
      // No receiver_id: the backend resolves the recipient from the post so an
      // anonymous author's identity is never exposed to the client.
      await inquiries.create({
        entity_type: "post",
        entity_id: post.id,
        message: inquireMsg.trim(),
      });
      setInquireOpen(false);
      setInquireMsg("");
    } catch (err) {
      setInquireError(err instanceof Error ? err.message : t("common.error"));
    }
    finally { setInquireLoading(false); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      await postsApi.addComment(post.id, { content: commentText.trim() });
      const data = await postsApi.listComments(post.id);
      setComments(data ?? []);
      setCommentText("");
    } catch {
      // ignore
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${meta.color}`,
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{ pb: 0, pt: 2, cursor: "pointer" }}
        onClick={() => navigate(`/app/post/${post.id}`)}
      >
        {/* Author row */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1.5 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: post.is_anonymous ? "#9CA3AF" : avatarColor(avatarLetter),
              fontSize: "0.9rem",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {avatarLetter}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              lineHeight={1.2}
              sx={{ color: "#1A1A2E" }}
              noWrap
            >
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              {timeAgo(post.created_at, t)}
            </Typography>
          </Box>
          {/* Post type chip — gradient pill */}
          <Chip
            label={t(meta.key)}
            size="small"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              background: meta.gradient,
              color: "white",
              height: 22,
              flexShrink: 0,
              borderRadius: "6px",
            }}
          />
        </Box>

        {/* Content */}
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.65,
            color: "#374151",
            fontSize: "0.9rem",
          }}
        >
          {post.content}
        </Typography>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ px: 2, pt: 1, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
          <Box
            onClick={user ? handleLike : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.4,
              py: 0.5,
              borderRadius: 20,
              cursor: user ? "pointer" : "default",
              border: `1.5px solid ${liked ? "transparent" : alpha("#B91C2C", 0.3)}`,
              backgroundImage: liked ? "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" : "none",
              bgcolor: liked ? "transparent" : alpha("#B91C2C", 0.06),
              color: liked ? "#fff" : "#B91C2C",
              boxShadow: liked ? `0 3px 10px ${alpha("#B91C2C", 0.35)}` : "none",
              transition: "all 0.15s",
              "&:hover": user ? {
                bgcolor: liked ? undefined : alpha("#B91C2C", 0.12),
                borderColor: "#B91C2C",
              } : {},
            }}
          >
            {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
              {likesCount}
            </Typography>
          </Box>
          <Box
            onClick={handleToggleComments}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.4,
              py: 0.5,
              borderRadius: 20,
              cursor: "pointer",
              border: `1.5px solid ${commentsOpen ? "transparent" : alpha("#0F2156", 0.3)}`,
              backgroundImage: commentsOpen ? "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)" : "none",
              bgcolor: commentsOpen ? "transparent" : alpha("#0F2156", 0.06),
              color: commentsOpen ? "#fff" : "#0F2156",
              boxShadow: commentsOpen ? `0 3px 10px ${alpha("#0F2156", 0.35)}` : "none",
              transition: "all 0.15s",
              "&:hover": {
                bgcolor: commentsOpen ? undefined : alpha("#0F2156", 0.12),
                borderColor: "#0F2156",
              },
            }}
          >
            <CommentIcon fontSize="small" />
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
              {post.comments_count ?? comments.length}
            </Typography>
          </Box>
          {user && post.creator_id !== user.id && (
            <Box
              onClick={() => { setInquireError(null); setInquireOpen(true); }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                ml: "auto",
                px: 1.4,
                py: 0.5,
                borderRadius: 20,
                cursor: "pointer",
                border: `1.5px solid ${alpha("#B9781A", 0.35)}`,
                bgcolor: alpha("#E8A020", 0.1),
                color: "#8A5D14",
                transition: "all 0.15s",
                "&:hover": {
                  backgroundImage: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                  bgcolor: "transparent",
                  color: "#fff",
                  borderColor: "transparent",
                  boxShadow: `0 3px 10px ${alpha("#B9781A", 0.35)}`,
                },
              }}
            >
              <MessageIcon sx={{ fontSize: "0.9rem" }} />
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                {t("business.inquire")}
              </Typography>
            </Box>
          )}
        </Box>
      </CardActions>

      {/* Comments section */}
      <Collapse in={commentsOpen}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
          {comments.length === 0 && commentsLoaded && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
              {t("common.noResults")}
            </Typography>
          )}
          {[...comments].reverse().map((c) => (
            <Box key={c.id} sx={{ mb: 1.5, display: "flex", gap: 1 }}>
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor: c.user ? avatarColor(c.user.first_name.charAt(0)) : "#9CA3AF",
                  flexShrink: 0,
                }}
              >
                {c.user ? c.user.first_name.charAt(0) : "?"}
              </Avatar>
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: "0 10px 10px 10px",
                  px: 1.5,
                  py: 0.75,
                  border: "1px solid #F0EDEA",
                  flex: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#1A1A2E", fontSize: "0.73rem" }}>
                    {c.user ? `${c.user.first_name} ${c.user.last_name}` : "User"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.68rem" }}>
                    {timeAgo(c.created_at, t)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#374151", mt: 0.2 }}>
                  {c.content}
                </Typography>
              </Box>
            </Box>
          ))}
          {user && (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor: avatarColor(user.first_name.charAt(0)),
                  flexShrink: 0,
                  alignSelf: "center",
                }}
              >
                {user.first_name.charAt(0)}
              </Avatar>
              <TextField
                size="small"
                fullWidth
                placeholder={t("feed.writeComment")}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 20,
                    bgcolor: "white",
                  },
                  // 16px prevents iOS Safari from auto-zooming the page on focus
                  "& .MuiOutlinedInput-input": {
                    fontSize: "16px",
                  },
                }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleAddComment}
                disabled={submittingComment || !commentText.trim()}
                sx={{
                  borderRadius: 20, minWidth: "auto", px: 1.5, alignSelf: "center",
                  color: "white",
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" },
                }}
              >
                {t("common.post")}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Inquire Dialog */}
      <Dialog open={inquireOpen} onClose={() => setInquireOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("business.sendInquiry")}</DialogTitle>
        <DialogContent>
          {inquireError && (
            <Alert severity="error" sx={{ mt: 1 }}>{inquireError}</Alert>
          )}
          {post.is_anonymous && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {t("business.inquiryToAnonymous")}
            </Typography>
          )}
          <TextField
            label={t("business.inquiryMessage")}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={inquireMsg}
            onChange={(e) => setInquireMsg(e.target.value)}
            placeholder={t("business.inquiryPlaceholder")}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInquireOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleInquire}
            disabled={inquireLoading || !inquireMsg.trim()}
            sx={{
              color: "white",
              "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" },
            }}
          >
            {inquireLoading ? t("common.sending") : t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
